import type { SourcePackFileResponse } from '@malaberg/shared';
import { SOURCE_PACK_CONTENT_MAX_BYTES } from '@malaberg/shared';
import {
  Box,
  Button,
  Group,
  Modal,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
/**
 * SourcePackTab — T28 (system_owner only).
 *
 * Lists source-pack files with filename, current version, and last edited date.
 * Per-row actions: Edit (opens MarkdownEditor), History (loads prior versions),
 * Delete (requires confirmation).
 * New file button opens MarkdownEditor in create mode.
 *
 * All writes create a new version via the API and refresh the list on success.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { sourcePackFilesApi } from '../../api/endpoints/source-pack-files';
import { MarkdownEditor } from '../../components/settings/MarkdownEditor';
import { VersionHistoryExpander } from '../../components/settings/VersionHistoryExpander';
import type { VersionEntry } from '../../components/settings/VersionHistoryExpander';
import { useProducts } from '../../hooks/use-products';

// ─── State types ──────────────────────────────────────────────────────────────

interface EditorState {
  mode: 'create' | 'edit';
  file: SourcePackFileResponse | null;
  filename: string;
  content: string;
  /** 'workspace' or 'product:<slug>'. Defaults to 'workspace' on create. */
  scope: string;
}

interface HistoryState {
  fileId: string;
  versions: VersionEntry[];
}

// ─── Byte length helper ───────────────────────────────────────────────────────

function byteLength(str: string): number {
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(str).length;
  }
  return encodeURIComponent(str).replace(/%[0-9A-Fa-f]{2}/g, '_').length;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function SourcePackTab() {
  const { products } = useProducts();
  const [files, setFiles] = useState<SourcePackFileResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<SourcePackFileResponse | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [history, setHistory] = useState<HistoryState | null>(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const loadFiles = useCallback(async () => {
    try {
      const res = await sourcePackFilesApi.list();
      if (isMounted.current) {
        const response = res as {
          ok: boolean;
          files?: SourcePackFileResponse[];
          items?: SourcePackFileResponse[];
        };
        setFiles(response.files ?? response.items ?? []);
      }
    } catch {
      if (isMounted.current) {
        notifications.show({
          title: 'Error',
          message: 'Failed to load source-pack files',
          color: 'red',
        });
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    void loadFiles();
  }, [loadFiles]);

  // ── Editor open/close ──────────────────────────────────────────────────────

  function openCreate() {
    setEditor({
      mode: 'create',
      file: null,
      filename: '',
      content: '',
      scope: 'workspace',
    });
    setHistory(null);
  }

  async function openEdit(file: SourcePackFileResponse) {
    setHistory(null);
    setEditor({
      mode: 'edit',
      file,
      filename: file.filename,
      content: '',
      scope: file.scope ?? 'workspace',
    });
    try {
      const res = (await sourcePackFilesApi.get(file.id)) as {
        ok: boolean;
        file?: SourcePackFileResponse;
        content?: string;
      };
      const full = res.file ?? (res as unknown as SourcePackFileResponse);
      if (isMounted.current) {
        setEditor({
          mode: 'edit',
          file: full,
          filename: full.filename ?? file.filename,
          content: full.content ?? '',
          scope: full.scope ?? file.scope ?? 'workspace',
        });
      }
    } catch (err) {
      if (isMounted.current) {
        notifications.show({
          title: 'Error',
          message: err instanceof Error ? err.message : 'Failed to load file content',
          color: 'red',
        });
        setEditor(null);
      }
    }
  }

  function closeEditor() {
    setEditor(null);
  }

  // ── Save (create or update) ────────────────────────────────────────────────

  async function handleSave() {
    if (!editor) return;

    const { mode, file, filename, content } = editor;
    if (!content.trim()) return;
    if (byteLength(content) > SOURCE_PACK_CONTENT_MAX_BYTES) return;

    setSaving(true);
    try {
      if (mode === 'create') {
        await sourcePackFilesApi.create({
          filename,
          content,
          scope: editor?.scope ?? 'workspace',
        });
      } else if (mode === 'edit' && file) {
        await sourcePackFilesApi.update(file.id, {
          filename,
          content,
          ...(editor?.scope ? { scope: editor.scope } : {}),
        });
      }

      notifications.show({
        title: 'File saved',
        message: `${filename || 'File'} has been saved.`,
        color: 'green',
      });

      closeEditor();
      await loadFiles();
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to save file',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  }

  // ── History ───────────────────────────────────────────────────────────────

  async function handleLoadHistory(file: SourcePackFileResponse) {
    try {
      const res = await sourcePackFilesApi.getVersions(file.id);
      const response = res as { ok?: boolean; items?: VersionEntry[] } | VersionEntry[];
      const items = Array.isArray(response)
        ? response
        : ((response as { items?: VersionEntry[] }).items ?? []);
      setHistory({ fileId: file.id, versions: items });
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to load version history',
        color: 'red',
      });
    }
  }

  // ── Delete ────────────────────────────────────────────────────────────────

  async function handleConfirmDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await sourcePackFilesApi.delete(deleteTarget.id);
      notifications.show({
        title: 'File deleted',
        message: `${deleteTarget.filename} has been deleted.`,
        color: 'green',
      });
      setDeleteTarget(null);
      await loadFiles();
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to delete file',
        color: 'red',
      });
    } finally {
      setDeleting(false);
    }
  }

  // ── Revert ────────────────────────────────────────────────────────────────

  async function handleRevert(fileId: string, version: number) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any
    await (sourcePackFilesApi as any).revert(fileId, version);
    await loadFiles();
  }

  // ── Derived ───────────────────────────────────────────────────────────────

  const editorBytes = editor ? byteLength(editor.content) : 0;
  const editorOverLimit = editorBytes > SOURCE_PACK_CONTENT_MAX_BYTES;
  const editorEmpty = !editor?.content.trim();
  const saveDisabled = editorEmpty || editorOverLimit || saving;

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <Title order={3}>Source pack files</Title>
        <Button data-testid="new-file-btn" onClick={openCreate}>
          New file
        </Button>
      </Group>

      {/* Editor modal */}
      {editor && (
        <Modal
          opened
          onClose={closeEditor}
          title={
            editor.mode === 'create' ? 'New source-pack file' : `Edit — ${editor.file?.filename}`
          }
          size="xl"
        >
          <Stack gap="sm">
            <TextInput
              label="Filename"
              placeholder="OPERATING_MANUAL.md"
              value={editor.filename}
              onChange={(e) => setEditor({ ...editor, filename: e.currentTarget.value })}
              data-testid="filename-input"
            />
            <Select
              label="Scope"
              description={
                editor.scope === 'workspace'
                  ? 'This file is offered to every product.'
                  : `Only available to product: ${editor.scope.replace('product:', '')}.`
              }
              data={[
                { value: 'workspace', label: 'Workspace — available to every product' },
                ...products.map((p) => ({
                  value: `product:${p.id}`,
                  label: `Product: ${p.name} (${p.id}) — only this product`,
                })),
              ]}
              value={editor.scope}
              onChange={(v) => setEditor({ ...editor, scope: v ?? 'workspace' })}
              data-testid="scope-select"
            />
            <MarkdownEditor
              value={editor.content}
              onChange={(v) => setEditor({ ...editor, content: v })}
            />
            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={closeEditor} disabled={saving}>
                Cancel
              </Button>
              <Button
                onClick={() => void handleSave()}
                loading={saving}
                disabled={saveDisabled}
                data-testid="editor-save-btn"
              >
                Save
              </Button>
            </Group>
          </Stack>
        </Modal>
      )}

      {/* Delete confirmation */}
      {deleteTarget && (
        <Modal
          opened
          onClose={() => setDeleteTarget(null)}
          title="Delete file"
          data-testid="delete-confirm-dialog"
        >
          <Text size="sm" mb="lg">
            Are you sure you want to delete <strong>{deleteTarget.filename}</strong>? This cannot be
            undone.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button
              color="red"
              loading={deleting}
              onClick={() => void handleConfirmDelete()}
              data-testid="delete-confirm-btn"
            >
              Delete
            </Button>
          </Group>
        </Modal>
      )}

      {/* File list */}
      {!loading && files.length === 0 ? (
        <Box data-testid="source-pack-empty">
          <Text c="dimmed">No source-pack files. Click "New file" to add one.</Text>
        </Box>
      ) : (
        <Table data-testid="source-pack-table" striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Filename</Table.Th>
              <Table.Th>Version</Table.Th>
              <Table.Th>Last edited</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {files.map((file) => (
              <Table.Tr key={file.id}>
                <Table.Td>{file.filename}</Table.Td>
                <Table.Td>v{file.version}</Table.Td>
                <Table.Td>{new Date(file.createdAt).toLocaleString()}</Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <Button
                      size="xs"
                      variant="light"
                      onClick={() => openEdit(file)}
                      data-testid="edit-file-btn"
                    >
                      Edit
                    </Button>
                    <Button
                      size="xs"
                      variant="light"
                      color="gray"
                      onClick={() => void handleLoadHistory(file)}
                      data-testid="history-btn"
                    >
                      History
                    </Button>
                    <Button
                      size="xs"
                      variant="light"
                      color="red"
                      onClick={() => setDeleteTarget(file)}
                      data-testid="delete-file-btn"
                    >
                      Delete
                    </Button>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      {/* Version history expander (shown when user clicks History for a file) */}
      {history && (
        <Box mt="md">
          <Title order={5} mb="sm">
            Version history
          </Title>
          <VersionHistoryExpander
            versions={history.versions}
            onRevert={(version) => handleRevert(history.fileId, version)}
          />
        </Box>
      )}
    </Stack>
  );
}
